import { Component, Input, computed, inject, signal } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CharacterTag, MAX_CHARACTER_TAGS } from '../../../../../core/models/editorialContent.model';
import { GroupIconComponent } from '../../../../../shared/components/group-icon/group-icon.component';

/** One tile: the term, the family it belongs to, and the artwork of that family. */
interface RenderedTag {
  code:      string;
  label:     string;
  iconKey:   string;
  /** Localised name of the visual group. Information the old pills threw away. */
  groupName: string;
  /** The dominant descriptor leads the set and is drawn at full width. */
  lead:      boolean;
}

/**
 * Character tiles: the dominant descriptors of a controlled vocabulary.
 *
 * They replace a row of pills. A pill is a filter affordance — it says "tap me to narrow a
 * list" — and these are not filters, they are what the spirit tastes of. Tiles say that
 * instead: the term is set large and is the protagonist of the tile, the family glyph is drawn
 * whole above it rather than cropped as a watermark, and the first descriptor takes the full
 * width because it is the headline of the set and not the first item of a list.
 *
 * Each tile carries the glyph of its GROUP, not of the term, which is the `group -> iconKey`
 * contract with the backend: a new term in an existing group needs no frontend change. The
 * group is also NAMED beside its glyph, so "Angélica" reads as a botanical to someone who has
 * never heard of it and the drawing gets glossed by the word at the same time.
 *
 * The maximum of five is applied upstream and again here. It is a product limit, so a contract
 * change or a hand-written response cannot turn a set of dominant traits into a list.
 */
@Component({
  selector: 'app-spirit-character-tags',
  standalone: true,
  imports: [TranslateModule, GroupIconComponent],
  templateUrl: './spirit-character-tags.component.html',
  styleUrl: './spirit-character-tags.component.scss'
})
export class SpiritCharacterTagsComponent {

  private translate = inject(TranslateService);

  private source = signal<CharacterTag[]>([]);

  @Input({ required: true }) set tags(value: CharacterTag[] | null | undefined) {
    this.source.set(value ?? []);
  }

  visible = computed<RenderedTag[]>(() =>
    this.source().slice(0, MAX_CHARACTER_TAGS).map((tag, index) => ({
      code:      tag.code,
      label:     tag.label,
      iconKey:   tag.iconKey,
      groupName: this.groupNameOf(tag.iconKey),
      lead:      index === 0
    }))
  );

  /**
   * A group with no translation yet renders without a caption rather than with a raw key: the
   * term is the information, the family is the gloss, and a gloss reading "botanical_new" is
   * worse than no gloss at all.
   */
  private groupNameOf(iconKey: string): string {
    const key = 'editorial.group.' + iconKey;
    const translated = this.translate.instant(key);

    return translated && translated !== key ? translated : '';
  }
}
